import { COMMERCE_SEGMENTS } from "./blueprints/commerce";
import { MODELS_SEGMENTS } from "./blueprints/models";
import { fashionModule, modelsModule } from "./instances";
import { getModuleHref } from "./registry";
import type { ModuleInstance } from "./registry/types";

/**
 * Tipli path üreticileri — hook dışı bağlamlar (store, servis, event
 * handler, statik config) için. Bileşen içinde aktif instance'a göre link
 * gerekiyorsa `useCommercePaths()` kullan (bkz. use-module-paths.ts).
 *
 * Hepsi `getModuleHref` üzerinden geçer: ana domainde `/fashion/shop`,
 * `shop.*` subdomain'inde `/shop`.
 */

const withQuery = (path: string, query?: Record<string, string | undefined>) => {
  if (!query) return path;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== "") params.set(k, v);
  });
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

/** Bir commerce instance'ı (fashion, technology…) için path seti. */
export const commercePaths = (instance: ModuleInstance) => ({
  instance,
  root: () => getModuleHref(instance, ""),
  shop: () => getModuleHref(instance, COMMERCE_SEGMENTS.shop),
  favorites: () => getModuleHref(instance, COMMERCE_SEGMENTS.favorites),
  search: (query?: Record<string, string | undefined>) =>
    withQuery(getModuleHref(instance, COMMERCE_SEGMENTS.search), query),
  product: (productId: string, query?: Record<string, string | undefined>) =>
    withQuery(
      getModuleHref(instance, `${COMMERCE_SEGMENTS.product}/${productId}`),
      query,
    ),
  wardrobe: () => getModuleHref(instance, COMMERCE_SEGMENTS.wardrobe),
  myWardrobe: () => getModuleHref(instance, COMMERCE_SEGMENTS.myWardrobe),
});

export type CommercePaths = ReturnType<typeof commercePaths>;

/** Varsayılan commerce instance'ı — modül dışından (cart, orders, chat) link verirken. */
export const fashionPaths = commercePaths(fashionModule);

/** Model Network tek instance; doğrudan kullanılır. */
export const modelsPaths = {
  instance: modelsModule,
  root: () => getModuleHref(modelsModule, ""),
  explore: () => getModuleHref(modelsModule, MODELS_SEGMENTS.explore),
  wizard: (
    modelId: string,
    chatId?: string | null,
    query?: Record<string, string | undefined>,
  ) =>
    withQuery(
      getModuleHref(
        modelsModule,
        `${MODELS_SEGMENTS.wizard}/${modelId}${chatId ? `/${chatId}` : ""}`,
      ),
      query,
    ),
  /** Breadcrumb dynamic-label anahtarı gibi "wizard kökü" gereken yerler için. */
  wizardBase: () => getModuleHref(modelsModule, MODELS_SEGMENTS.wizard),
  library: () => getModuleHref(modelsModule, MODELS_SEGMENTS.library),
  studio: () => getModuleHref(modelsModule, MODELS_SEGMENTS.studio),
  studioProjects: () =>
    getModuleHref(modelsModule, MODELS_SEGMENTS.studioProjects),
};
